import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { HiMenuAlt3 } from "react-icons/hi";
import { IoClose } from "react-icons/io5";
import logoD from "../assets/Planner.png";

const LNavbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const drawerRef = useRef(null);

  // Handle scroll effect for background transparency
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Sync body scroll with menu state
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isMobileMenuOpen]);

  // Close menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Features", path: "/#features" },
    { name: "Pricing", path: "/#pricing" },
    { name: "Contact", path: "/#contact" },
  ];

  return (
    <>
      <NavContainer className={scrolled ? "scrolled" : ""}>
        <NavContent>
          <LogoLink to="/">
            <img src={logoD} alt="Planner Logo" />
          </LogoLink>

          <NavLinks>
            {navLinks.map((link) => (
              <NavLinkItem key={link.name}>
                <Link
                  to={link.path}
                  className={location.pathname === link.path ? "active" : ""}
                >
                  {link.name}
                  <span className="underline" />
                </Link>
              </NavLinkItem>
            ))}
          </NavLinks>

          <ActionGroup>
            <AuthButtons>
              <Link to="/login" className="login-btn">
                Log In
              </Link>
              <Link to="/signup" className="get-started-btn">
                GET STARTED
              </Link>
            </AuthButtons>

            <MobileMenuButton onClick={toggleMobileMenu}>
              <HiMenuAlt3 />
            </MobileMenuButton>
          </ActionGroup>
        </NavContent>
      </NavContainer>

      {/* Mobile Drawer UI */}
      <MobileOverlay $isOpen={isMobileMenuOpen} onClick={toggleMobileMenu} />
      <MobileDrawer ref={drawerRef} $isOpen={isMobileMenuOpen}>
        <DrawerHeader>
          <DrawerClose onClick={toggleMobileMenu}>
            <IoClose />
          </DrawerClose>
        </DrawerHeader>
        <DrawerContent>
          <DrawerLinks>
            {navLinks.map((link) => (
              <DrawerLinkItem key={link.name}>
                <Link
                  to={link.path}
                  onClick={toggleMobileMenu}
                  className={location.pathname === link.path ? "active" : ""}
                >
                  {link.name}
                </Link>
              </DrawerLinkItem>
            ))}
          </DrawerLinks>
          <DrawerActions>
            <DrawerButton
              to="/login"
              onClick={toggleMobileMenu}
              className="login-btn"
            >
              Log In
            </DrawerButton>
            <DrawerButton
              to="/signup"
              onClick={toggleMobileMenu}
              className="get-started-btn"
            >
              GET STARTED
            </DrawerButton>
          </DrawerActions>
        </DrawerContent>
      </MobileDrawer>
    </>
  );
};

// --- STYLED COMPONENTS (Optimized) ---

const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 90px;
  z-index: 1000;
  transition: all 0.3s ease-in-out;
  padding: 0 5%;
  color: white;
  display: flex;
  align-items: center;

  &.scrolled {
    height: 70px; /* Shrinks slightly on scroll for a premium feel */
    background: #08040c32;
    backdrop-filter: blur(15px);
    -webkit-backdrop-filter: blur(15px);
    /* border-bottom: 1px solid rgba(255, 255, 255, 0.05); */
  }

  @media (max-width: 768px) {
    height: 70px;
    padding: 0 20px;
  }
`;

const NavContent = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoLink = styled(Link)`
  img {
    height: 50px;
    width: auto;
    display: block;
    @media (max-width: 768px) {
      height: 40px;
    }
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2.5rem;
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavLinkItem = styled.div`
  a {
    position: relative;
    text-decoration: none;
    color: white;
    font-size: 0.95rem;
    .underline {
      position: absolute;
      bottom: -4px;
      left: 0;
      width: 0;
      height: 2px;
      background: #9281dd;
      transition: width 0.3s ease;
    }
    &:hover .underline,
    &.active .underline {
      width: 100%;
    }
  }
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  @media (max-width: 1024px) {
    display: none;
  }
  .login-btn {
    text-decoration: none;
    color: white;
    font-weight: 600;
  }
  .get-started-btn {
    text-decoration: none;
    background: #9281dd;
    color: white;
    padding: 10px 24px;
    border-radius: 50px;
    font-weight: 600;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  @media (max-width: 1024px) {
    display: block;
  }
`;

const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  z-index: 9998;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: all 0.3s ease;
`;

const MobileDrawer = styled.div`
  position: fixed;
  top: 0;
  right: ${(props) => (props.$isOpen ? "0" : "-100%")};
  width: 80%;
  max-width: 350px;
  height: 100%;
  background: rgba(42, 20, 65, 0.589);
  z-index: 9999;
  transition: right 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const DrawerClose = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 2.5rem;
`;
const DrawerContent = styled.div`
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const DrawerLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const DrawerLinkItem = styled.div`
  a {
    color: white;
    text-decoration: none;
    font-size: 1.3rem;
    padding: 15px;
    display: block;
    border-radius: 10px;
    &.active {
      background: rgba(146, 129, 221, 0.2);
      color: #9281dd;
    }
  }
`;
const DrawerActions = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding-bottom: 40px;
`;
const DrawerButton = styled(Link)`
  text-align: center;
  padding: 15px;
  border-radius: 10px;
  text-decoration: none;
  font-weight: 600;
  &.login-btn {
    border: 1px solid white;
    color: white;
  }
  &.get-started-btn {
    background: #9281dd;
    color: white;
  }
`;

export default LNavbar;
